/**
 * Bridge from the in-app `sossiety:analytics` CustomEvent bus to PostHog.
 *
 * Producers (e.g. `usePremiumTriggerAnalytics`) dispatch:
 *   window.dispatchEvent(new CustomEvent('sossiety:analytics', {
 *     detail: { name: 'premium_trigger_shown', ...properties }
 *   }))
 *
 * This bridge listens once and forwards every event to `capture`. No producer
 * changes are needed when a new event is added on the dispatching side.
 */

import { capture } from './posthog';
import { ANALYTICS_BUS_EVENT } from './events';

let attached = false;

function handler(event: Event) {
  const detail = (event as CustomEvent).detail as { name?: unknown } & Record<string, unknown>;
  if (typeof detail?.name !== 'string') return;
  const { name, ...properties } = detail as { name: string } & Record<string, unknown>;
  capture(name, properties);
}

/**
 * Attach the bus listener once for the page lifetime. The returned cleanup
 * is only useful for tests/HMR; production code never calls it.
 */
export function setupAnalyticsBridge(): () => void {
  if (typeof window === 'undefined' || attached) {
    return () => undefined;
  }
  window.addEventListener(ANALYTICS_BUS_EVENT, handler);
  attached = true;
  return () => {
    if (typeof window === 'undefined') return;
    window.removeEventListener(ANALYTICS_BUS_EVENT, handler);
    attached = false;
  };
}
